import {
  CardCatalogSchema,
  DeckDefinitionSchema,
  createEngineGameplayCatalogV3,
  expandDeckDefinition,
  validateDeck,
  type CardCatalog,
  type DeckDefinition,
} from "@fate-kingdom/card-data";

/** Test-only cards that make one complete, deterministic Core Battle practical. */
export const CORE_BATTLE_MATCH_CARD_DATA_VERSION = "0.7.0-core-battle-match.1";
/** Test-only no-cheating bot fixture; it is not release card or balance content. */
export const RANDOM_LEGAL_CORE_BATTLE_CARD_DATA_VERSION = "0.7.1-random-legal-bot.1";

export const CORE_BATTLE_MATCH_CARD_IDS = {
  plains: "card.core-battle-match-plains",
  forest: "card.core-battle-match-forest",
  hills: "card.core-battle-match-hills",
  river: "card.core-battle-match-river",
  coast: "card.core-battle-match-coast",
  assaultCommander: "card.core-battle-match-assault-commander",
  balancedCommander: "card.core-battle-match-balanced-commander",
  assaultArmy1: "card.core-battle-match-assault-army-1",
  assaultArmy2: "card.core-battle-match-assault-army-2",
  assaultArmy3: "card.core-battle-match-assault-army-3",
  assaultArmy4: "card.core-battle-match-assault-army-4",
  assaultArmy5: "card.core-battle-match-assault-army-5",
  assaultArmy6: "card.core-battle-match-assault-army-6",
  assaultArmy7: "card.core-battle-match-assault-army-7",
  assaultArmy8: "card.core-battle-match-assault-army-8",
  balancedArmy1: "card.core-battle-match-balanced-army-1",
  balancedArmy2: "card.core-battle-match-balanced-army-2",
  balancedArmy3: "card.core-battle-match-balanced-army-3",
  balancedArmy4: "card.core-battle-match-balanced-army-4",
  balancedArmy5: "card.core-battle-match-balanced-army-5",
  balancedArmy6: "card.core-battle-match-balanced-army-6",
  balancedArmy7: "card.core-battle-match-balanced-army-7",
  balancedArmy8: "card.core-battle-match-balanced-army-8",
} as const;

const CORE_BATTLE_MATCH_LAND_IDS = [
  CORE_BATTLE_MATCH_CARD_IDS.plains,
  CORE_BATTLE_MATCH_CARD_IDS.forest,
  CORE_BATTLE_MATCH_CARD_IDS.hills,
  CORE_BATTLE_MATCH_CARD_IDS.river,
  CORE_BATTLE_MATCH_CARD_IDS.coast,
] as const;

const CORE_BATTLE_MATCH_ASSAULT_ARMY_IDS = [
  CORE_BATTLE_MATCH_CARD_IDS.assaultArmy1,
  CORE_BATTLE_MATCH_CARD_IDS.assaultArmy2,
  CORE_BATTLE_MATCH_CARD_IDS.assaultArmy3,
  CORE_BATTLE_MATCH_CARD_IDS.assaultArmy4,
  CORE_BATTLE_MATCH_CARD_IDS.assaultArmy5,
  CORE_BATTLE_MATCH_CARD_IDS.assaultArmy6,
  CORE_BATTLE_MATCH_CARD_IDS.assaultArmy7,
  CORE_BATTLE_MATCH_CARD_IDS.assaultArmy8,
] as const;

const CORE_BATTLE_MATCH_BALANCED_ARMY_IDS = [
  CORE_BATTLE_MATCH_CARD_IDS.balancedArmy1,
  CORE_BATTLE_MATCH_CARD_IDS.balancedArmy2,
  CORE_BATTLE_MATCH_CARD_IDS.balancedArmy3,
  CORE_BATTLE_MATCH_CARD_IDS.balancedArmy4,
  CORE_BATTLE_MATCH_CARD_IDS.balancedArmy5,
  CORE_BATTLE_MATCH_CARD_IDS.balancedArmy6,
  CORE_BATTLE_MATCH_CARD_IDS.balancedArmy7,
  CORE_BATTLE_MATCH_CARD_IDS.balancedArmy8,
] as const;

function makeLand(id: string, name: string, landTypeId: string) {
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id,
    type: "land",
    name: `TEST ONLY ${name}`,
    cost: {},
    rulesText: "TEST ONLY: zero-cost Core Battle proof Land.",
    landTypeId,
    developmentId: id,
    stages: [
      {
        stage: 1,
        title: `${name} Stage I`,
        maximumControl: 3,
        production: { food: 2 },
        effects: [],
      },
      {
        stage: 2,
        title: `${name} Stage II`,
        maximumControl: 5,
        production: { food: 3 },
        effects: [],
      },
      {
        stage: 3,
        title: `${name} Stage III`,
        maximumControl: 7,
        production: { food: 4 },
        effects: [],
      },
    ],
  };
}

function makeCommander(
  id: string,
  name: string,
  stats: { readonly attack: number; readonly guard: number; readonly armyCapacity: number },
) {
  const key = id.replace("card.", "");
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id,
    type: "commander",
    name: `TEST ONLY ${name}`,
    cost: {},
    rulesText: "TEST ONLY: zero-cost Core Battle proof Commander with an inert Mandate.",
    stats,
    mandates: [
      {
        mandateId: `mandate.${key}`,
        name: `${name} inert Mandate`,
        condition: { conditionId: `condition.${key}` },
        effects: [{ effectId: `effect.${key}` }],
      },
    ],
  };
}

function makeArmy(id: string, name: string, attack: number) {
  return {
    schemaVersion: 1,
    definitionVersion: 1,
    id,
    type: "army",
    name: `TEST ONLY ${name}`,
    cost: {},
    rulesText: "TEST ONLY: zero-cost, zero-Upkeep Core Battle proof Army.",
    stats: { armySize: 1, attack, guard: 0 },
    upkeep: {},
    effects: [],
  };
}

const commanders = [
  makeCommander(CORE_BATTLE_MATCH_CARD_IDS.assaultCommander, "Assault Commander", {
    attack: 3,
    guard: 0,
    armyCapacity: 3,
  }),
  makeCommander(CORE_BATTLE_MATCH_CARD_IDS.balancedCommander, "Balanced Commander", {
    attack: 1,
    guard: 0,
    armyCapacity: 3,
  }),
];

const assaultArmies = CORE_BATTLE_MATCH_ASSAULT_ARMY_IDS.map((id, index) =>
  makeArmy(id, `Assault Army ${index + 1}`, 1),
);
const balancedArmies = CORE_BATTLE_MATCH_BALANCED_ARMY_IDS.map((id, index) =>
  makeArmy(id, `Balanced Army ${index + 1}`, 1),
);

/**
 * This catalog intentionally contains only cards the current Core Battle
 * engine can execute. It is test data, never release card content.
 */
export const CORE_BATTLE_MATCH_CARD_CATALOG: CardCatalog = CardCatalogSchema.parse({
  schemaVersion: 1,
  cardDataVersion: CORE_BATTLE_MATCH_CARD_DATA_VERSION,
  cards: [
    makeLand(CORE_BATTLE_MATCH_CARD_IDS.plains, "Plains", "plains"),
    makeLand(CORE_BATTLE_MATCH_CARD_IDS.forest, "Forest", "forest"),
    makeLand(CORE_BATTLE_MATCH_CARD_IDS.hills, "Hills", "hills"),
    makeLand(CORE_BATTLE_MATCH_CARD_IDS.river, "River", "river"),
    makeLand(CORE_BATTLE_MATCH_CARD_IDS.coast, "Coast", "coast"),
    ...commanders,
    ...assaultArmies,
    ...balancedArmies,
  ],
  conditions: commanders.map((commander) => ({
    schemaVersion: 1,
    definitionVersion: 1,
    id: `condition.${commander.id.replace("card.", "")}`,
    rulesText: "TEST ONLY: inert Core Battle proof Mandate condition.",
  })),
  effects: commanders.map((commander) => ({
    schemaVersion: 1,
    definitionVersion: 1,
    id: `effect.${commander.id.replace("card.", "")}`,
    rulesText: "TEST ONLY: inert Core Battle proof Mandate effect.",
  })),
  triggers: [],
});

export const CORE_BATTLE_MATCH_ENGINE_CATALOG = createEngineGameplayCatalogV3(
  CORE_BATTLE_MATCH_CARD_CATALOG,
);

/**
 * A compact test-only Core Battle catalog for bounded Random Legal Bot proof.
 * It changes no release data: 1-Control Lands and 4-Attack Commanders ensure
 * that a random but legal attack can demonstrate terminal match handling.
 */
export const RANDOM_LEGAL_CORE_BATTLE_CARD_CATALOG: CardCatalog = CardCatalogSchema.parse({
  ...CORE_BATTLE_MATCH_CARD_CATALOG,
  cardDataVersion: RANDOM_LEGAL_CORE_BATTLE_CARD_DATA_VERSION,
  cards: CORE_BATTLE_MATCH_CARD_CATALOG.cards.map((card) => {
    if (card.type === "land") {
      return {
        ...card,
        stages: card.stages.map((stage) => ({ ...stage, maximumControl: 1 })),
      };
    }
    if (card.type === "commander") {
      return { ...card, stats: { ...card.stats, attack: 4, guard: 0 } };
    }
    return card;
  }),
});

export const RANDOM_LEGAL_CORE_BATTLE_ENGINE_CATALOG = createEngineGameplayCatalogV3(
  RANDOM_LEGAL_CORE_BATTLE_CARD_CATALOG,
);

function parseDeck(input: unknown): DeckDefinition {
  const deck = DeckDefinitionSchema.parse(input);
  const validated = validateDeck(deck, CORE_BATTLE_MATCH_CARD_CATALOG);
  if (!validated.success) {
    throw new Error(`Invalid Core Battle proof deck: ${validated.error.message}`);
  }
  return validated.data;
}

function makeDeck(
  id: string,
  name: string,
  commanderId: string,
  armyIds: readonly string[],
): DeckDefinition {
  return parseDeck({
    schemaVersion: 1,
    definitionVersion: 1,
    cardDataVersion: CORE_BATTLE_MATCH_CARD_DATA_VERSION,
    id,
    name: `TEST ONLY: ${name}`,
    cards: [
      ...CORE_BATTLE_MATCH_LAND_IDS.map((cardId) => ({ cardId, count: 3 })),
      { cardId: commanderId, count: 3 },
      ...armyIds.slice(0, 7).map((cardId) => ({ cardId, count: 3 })),
      { cardId: armyIds[7], count: 1 },
    ],
  });
}

/** Distinct legal 40-card decks, limited to Land, Commander, and Army cards. */
export const CORE_BATTLE_MATCH_TEST_DECKS = {
  balanced: makeDeck(
    "deck.core-battle-match-balanced",
    "Core Battle Balanced",
    CORE_BATTLE_MATCH_CARD_IDS.balancedCommander,
    CORE_BATTLE_MATCH_BALANCED_ARMY_IDS,
  ),
  assault: makeDeck(
    "deck.core-battle-match-assault",
    "Core Battle Assault",
    CORE_BATTLE_MATCH_CARD_IDS.assaultCommander,
    CORE_BATTLE_MATCH_ASSAULT_ARMY_IDS,
  ),
} as const;

export const CORE_BATTLE_MATCH_DECK_CARD_IDS = {
  balanced: expandDeckDefinition(CORE_BATTLE_MATCH_TEST_DECKS.balanced),
  assault: expandDeckDefinition(CORE_BATTLE_MATCH_TEST_DECKS.assault),
} as const;

/**
 * Creates the setup used by the complete-match regression and the Node CLI.
 * The return value is deliberately plain data so testkit remains independent
 * from the engine package.
 */
export function createCoreBattleMatchSetup(
  seed = "core-battle-proof-364",
  matchId = "core-battle-match-alpha-1",
) {
  return {
    matchId,
    seed,
    rulesVersion: "first-edition" as const,
    cardDataVersion: CORE_BATTLE_MATCH_CARD_DATA_VERSION,
    gameplayCatalogHash: CORE_BATTLE_MATCH_ENGINE_CATALOG.gameplayCatalogHash,
    catalog: CORE_BATTLE_MATCH_ENGINE_CATALOG,
    decks: {
      "player-1": {
        deckId: CORE_BATTLE_MATCH_TEST_DECKS.assault.id,
        cardDefinitionIds: CORE_BATTLE_MATCH_DECK_CARD_IDS.assault,
      },
      "player-2": {
        deckId: CORE_BATTLE_MATCH_TEST_DECKS.balanced.id,
        cardDefinitionIds: CORE_BATTLE_MATCH_DECK_CARD_IDS.balanced,
      },
    },
  };
}

/**
 * Fresh-setup test data for Random Legal Bot self-play. The rules engine and
 * deck legality are unchanged; only this unshipped fixture shortens a random
 * proof enough to keep the deterministic smoke and batch bounded.
 */
export function createRandomLegalCoreBattleMatchSetup(
  seed = "random-legal-core-battle-001",
  matchId = "random-legal-core-battle-alpha-1",
) {
  return {
    matchId,
    seed,
    rulesVersion: "first-edition" as const,
    cardDataVersion: RANDOM_LEGAL_CORE_BATTLE_CARD_DATA_VERSION,
    gameplayCatalogHash: RANDOM_LEGAL_CORE_BATTLE_ENGINE_CATALOG.gameplayCatalogHash,
    catalog: RANDOM_LEGAL_CORE_BATTLE_ENGINE_CATALOG,
    decks: {
      "player-1": {
        deckId: CORE_BATTLE_MATCH_TEST_DECKS.assault.id,
        cardDefinitionIds: CORE_BATTLE_MATCH_DECK_CARD_IDS.assault,
      },
      "player-2": {
        deckId: CORE_BATTLE_MATCH_TEST_DECKS.balanced.id,
        cardDefinitionIds: CORE_BATTLE_MATCH_DECK_CARD_IDS.balanced,
      },
    },
  };
}

type CoreBattleProofPlayerId = "player-1" | "player-2";
type CoreBattleProofLaneId = "left" | "center" | "right";

export type CoreBattleProofAction =
  | { readonly type: "advance-phase"; readonly playerId: CoreBattleProofPlayerId }
  | {
      readonly type: "found-land" | "develop-land";
      readonly playerId: CoreBattleProofPlayerId;
      readonly cardInstanceId: string;
      readonly laneId: CoreBattleProofLaneId;
    }
  | {
      readonly type: "deploy-commander";
      readonly playerId: CoreBattleProofPlayerId;
      readonly commanderCardInstanceId: string;
      readonly laneId: CoreBattleProofLaneId;
    }
  | {
      readonly type: "deploy-army";
      readonly playerId: CoreBattleProofPlayerId;
      readonly armyCardInstanceId: string;
      readonly laneId: CoreBattleProofLaneId;
    }
  | {
      readonly type: "attack";
      readonly playerId: CoreBattleProofPlayerId;
      readonly laneId: CoreBattleProofLaneId;
    };

export interface CoreBattleProofState {
  readonly activePlayerId: CoreBattleProofPlayerId;
  readonly phase: string;
  readonly lanes: Readonly<
    Record<
      CoreBattleProofLaneId,
      {
        readonly players: Readonly<
          Record<
            CoreBattleProofPlayerId,
            {
              readonly status: string;
              readonly land: { readonly stage: number } | null;
              readonly commander: unknown | null;
              readonly armies: readonly unknown[];
            }
          >
        >;
      }
    >
  >;
}

function choose(
  legalActions: readonly CoreBattleProofAction[],
  predicate: (action: CoreBattleProofAction) => boolean,
): CoreBattleProofAction | undefined {
  return legalActions.find(predicate);
}

/**
 * Fixed-priority test script, not a bot. It reads the authoritative legal
 * action list and returns one of those values verbatim. The runner never calls
 * this itself; tests and the CLI inject it explicitly.
 */
export function getCoreBattleProofAction(
  state: CoreBattleProofState,
  legalActions: readonly CoreBattleProofAction[],
): CoreBattleProofAction | null {
  const playerId = state.activePlayerId;
  let action: CoreBattleProofAction | undefined;

  if (state.phase === "land") {
    const left = state.lanes.left.players[playerId];
    const center = state.lanes.center.players[playerId];
    const right = state.lanes.right.players[playerId];

    if (playerId === "player-1") {
      action =
        (!left.land
          ? choose(
              legalActions,
              (candidate) => candidate.type === "found-land" && candidate.laneId === "left",
            )
          : undefined) ??
        (!center.land
          ? choose(
              legalActions,
              (candidate) => candidate.type === "found-land" && candidate.laneId === "center",
            )
          : undefined) ??
        (!right.land
          ? choose(
              legalActions,
              (candidate) => candidate.type === "found-land" && candidate.laneId === "right",
            )
          : undefined) ??
        (left.land?.stage === 1
          ? choose(
              legalActions,
              (candidate) => candidate.type === "develop-land" && candidate.laneId === "left",
            )
          : undefined);
    } else {
      action =
        (!left.land
          ? choose(
              legalActions,
              (candidate) => candidate.type === "found-land" && candidate.laneId === "left",
            )
          : undefined) ??
        (!center.land
          ? choose(
              legalActions,
              (candidate) => candidate.type === "found-land" && candidate.laneId === "center",
            )
          : undefined);
    }
  } else if (
    state.phase === "command" &&
    playerId === "player-1" &&
    state.lanes.left.players[playerId].land?.stage === 2
  ) {
    const left = state.lanes.left.players[playerId];
    const center = state.lanes.center.players[playerId];
    action =
      (left.status === "active" && left.commander === null
        ? choose(
            legalActions,
            (candidate) => candidate.type === "deploy-commander" && candidate.laneId === "left",
          )
        : undefined) ??
      (left.status === "active" && left.commander !== null && left.armies.length === 0
        ? choose(
            legalActions,
            (candidate) => candidate.type === "deploy-army" && candidate.laneId === "left",
          )
        : undefined) ??
      (center.status === "active" && center.commander === null
        ? choose(
            legalActions,
            (candidate) => candidate.type === "deploy-commander" && candidate.laneId === "center",
          )
        : undefined);
  } else if (state.phase === "battle" && playerId === "player-1") {
    action =
      choose(
        legalActions,
        (candidate) => candidate.type === "attack" && candidate.laneId === "left",
      ) ??
      choose(
        legalActions,
        (candidate) => candidate.type === "attack" && candidate.laneId === "center",
      );
  }

  return action ?? choose(legalActions, (candidate) => candidate.type === "advance-phase") ?? null;
}
